import { useEffect, useState } from "react";
import api from "../lib/api";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSub, DropdownMenuSubContent, DropdownMenuSubTrigger, DropdownMenuTrigger } from "./ui/dropdown-menu";
import { Loader2, Sparkles, Wand2, RefreshCw } from "lucide-react";

const UNAVAILABLE = "Assistant IA momentanément indisponible, réessayez dans un instant";
const PRIVACY = "Ce texte est envoyé à un service d'IA : n'y mettez pas d'informations personnelles (noms, téléphones, santé…).";
const LANGS = [["fr", "Français"], ["en", "English"], ["wo", "Wolof"]];
const FREQ = { daily: "Quotidienne", weekly: "Hebdomadaire", monthly: "Mensuelle", custom: "Personnalisée" };
const MODES = { correct: "Corriger les fautes", rephrase: "Reformuler", shorten: "Raccourcir", translate: "Traduire" };

const errMsg = (err) => {
  const s = err?.response?.status;
  if (s === 429 || s === 403 || s === 400) return err.response.data?.detail || UNAVAILABLE;
  return UNAVAILABLE;
};

const callAI = (body) => api.post("/ai/description", body, { timeout: 20000 }).then((r) => r.data);

const ctxLines = (kind, c) => {
  const cur = "FCFA";
  const rows = [["Type", kind === "tontine" ? "Tontine" : "Cagnotte"], ["Titre", c.title]];
  if (kind === "cagnotte") rows.push(["Objectif", c.goal_amount && `${Number(c.goal_amount).toLocaleString("fr-FR")} ${cur}`]);
  else {
    rows.push(["Cotisation", c.contribution_amount && `${Number(c.contribution_amount).toLocaleString("fr-FR")} ${cur}`]);
    rows.push(["Fréquence", c.frequency === "custom" && c.frequency_days ? `Tous les ${c.frequency_days} jours` : FREQ[c.frequency]]);
    rows.push(["Participants / tours", c.max_participants]);
    rows.push(["Démarrage", c.start_date]);
  }
  rows.push(["Devise", cur], ["Date limite", c.end_date]);
  return rows.filter(([, v]) => v);
};

function OptionSelect({ label, value, onChange, options, testid }) {
  return (
    <div>
      <Label className="text-xs">{label}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="mt-1 h-10 rounded-xl" data-testid={testid}><SelectValue /></SelectTrigger>
        <SelectContent>{options.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );
}

function GenerateDialog({ open, onOpenChange, kind, context, onUse }) {
  const [opts, setOpts] = useState({ purpose: "", tone: "chaleureux", length: "moyenne", language: "fr" });
  const [result, setResult] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [remaining, setRemaining] = useState(null);
  const set = (k, v) => setOpts((o) => ({ ...o, [k]: v }));

  useEffect(() => { if (open) { setResult(""); setError(""); } }, [open]);

  const run = async () => {
    setLoading(true); setError("");
    try {
      const data = await callAI({ mode: "generate", kind, context, ...opts });
      setResult(data.text); setRemaining(data.remaining);
    } catch (err) { setError(errMsg(err)); } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[92vh] overflow-y-auto" data-testid="ai-generate-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Sparkles className="h-5 w-5 text-primary" />Rédiger avec l'IA</DialogTitle>
          <DialogDescription>L'assistant s'appuie uniquement sur les informations déjà saisies.</DialogDescription>
        </DialogHeader>
        <div className="rounded-xl bg-secondary/60 p-3 text-xs grid grid-cols-2 gap-x-4 gap-y-1" data-testid="ai-context-summary">
          {ctxLines(kind, context).map(([k, v]) => <div key={k} className="min-w-0 truncate"><span className="text-muted-foreground">{k} : </span><strong>{v}</strong></div>)}
        </div>
        <div>
          <Label htmlFor="ai-purpose" className="text-xs">En quelques mots, à quoi sert-elle ? (optionnel)</Label>
          <Textarea id="ai-purpose" rows={2} maxLength={500} value={opts.purpose} onChange={(e) => set("purpose", e.target.value)} className="mt-1 rounded-xl" data-testid="ai-purpose-input" />
          <p className="text-[11px] text-muted-foreground mt-1" data-testid="ai-privacy-notice">{PRIVACY}</p>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <OptionSelect label="Ton" value={opts.tone} onChange={(v) => set("tone", v)} testid="ai-tone-select" options={[["sobre", "Sobre"], ["chaleureux", "Chaleureux"], ["solidaire", "Solidaire"], ["festif", "Festif"]]} />
          <OptionSelect label="Longueur" value={opts.length} onChange={(v) => set("length", v)} testid="ai-length-select" options={[["courte", "Courte"], ["moyenne", "Moyenne"]]} />
          <OptionSelect label="Langue" value={opts.language} onChange={(v) => set("language", v)} testid="ai-language-select" options={LANGS} />
        </div>
        {error && <p className="text-sm text-destructive" data-testid="ai-error">{error}</p>}
        {result && (
          <div>
            <Label className="text-xs">Aperçu</Label>
            <div className="mt-1 rounded-xl border p-3 text-sm whitespace-pre-wrap max-h-60 overflow-y-auto" data-testid="ai-preview">{result}</div>
            {remaining !== null && <p className="text-[11px] text-muted-foreground mt-1" data-testid="ai-remaining">{remaining} utilisation(s) restante(s) aujourd'hui</p>}
          </div>
        )}
        <DialogFooter className="gap-2 sm:gap-2">
          <Button type="button" variant="outline" className="rounded-full" onClick={() => onOpenChange(false)} data-testid="ai-cancel-btn">Annuler</Button>
          <Button type="button" variant="outline" className="rounded-full" disabled={loading} onClick={run} data-testid="ai-generate-btn">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : result ? <><RefreshCw className="h-4 w-4 mr-1.5" />Régénérer</> : <><Sparkles className="h-4 w-4 mr-1.5" />Générer</>}
          </Button>
          {result && <Button type="button" className="btn-gradient rounded-full" onClick={() => { onUse(result); onOpenChange(false); }} data-testid="ai-use-btn">Utiliser ce texte</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ImproveDialog({ job, onClose, kind, value, onUse }) {
  const [result, setResult] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!job) return;
    setResult(""); setError(""); setLoading(true);
    callAI({ mode: job.mode, kind, text: value, language: job.language || "fr" })
      .then((d) => setResult(d.text)).catch((err) => setError(errMsg(err))).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job]);

  return (
    <Dialog open={!!job} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="ai-improve-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Wand2 className="h-5 w-5 text-primary" />{job ? MODES[job.mode] : ""}</DialogTitle>
          <DialogDescription>{PRIVACY}</DialogDescription>
        </DialogHeader>
        <div className="grid sm:grid-cols-2 gap-3">
          <div className="min-w-0">
            <Label className="text-xs">Avant</Label>
            <div className="mt-1 rounded-xl bg-secondary/60 p-3 text-sm whitespace-pre-wrap max-h-72 overflow-y-auto" data-testid="ai-before">{value}</div>
          </div>
          <div className="min-w-0">
            <Label className="text-xs">Après</Label>
            <div className="mt-1 rounded-xl border p-3 text-sm whitespace-pre-wrap max-h-72 overflow-y-auto min-h-[80px]" data-testid="ai-after">
              {loading ? <Loader2 className="h-5 w-5 animate-spin text-primary" /> : result}
            </div>
          </div>
        </div>
        {error && <p className="text-sm text-destructive" data-testid="ai-improve-error">{error}</p>}
        <DialogFooter className="gap-2 sm:gap-2">
          <Button type="button" variant="outline" className="rounded-full" onClick={onClose} data-testid="ai-improve-cancel-btn">Annuler</Button>
          <Button type="button" className="btn-gradient rounded-full" disabled={!result || loading} onClick={() => { onUse(result); onClose(); }} data-testid="ai-replace-btn">Remplacer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function AIDescriptionAssistant({ kind, context, value, onChange }) {
  const [enabled, setEnabled] = useState(false);
  const [genOpen, setGenOpen] = useState(false);
  const [job, setJob] = useState(null);

  useEffect(() => {
    api.get("/platform/public-config").then(({ data }) => setEnabled(data.ai_enabled !== false)).catch(() => setEnabled(false));
  }, []);

  if (!enabled) return null;
  const hasText = !!(value || "").trim();

  return (
    <div className="flex flex-wrap items-center gap-2" data-testid={`ai-assistant-${kind}`}>
      <Button type="button" size="sm" variant="outline" className="rounded-full h-8 text-xs" onClick={() => setGenOpen(true)} data-testid="ai-write-btn">
        <Sparkles className="h-3.5 w-3.5 mr-1.5 text-primary" />Rédiger avec l'IA
      </Button>
      {hasText && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button type="button" size="sm" variant="outline" className="rounded-full h-8 text-xs" data-testid="ai-improve-btn">
              <Wand2 className="h-3.5 w-3.5 mr-1.5 text-primary" />Améliorer
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {["correct", "rephrase", "shorten"].map((m) => (
              <DropdownMenuItem key={m} onSelect={() => setJob({ mode: m, at: Date.now() })} data-testid={`ai-improve-${m}`}>{MODES[m]}</DropdownMenuItem>
            ))}
            <DropdownMenuSub>
              <DropdownMenuSubTrigger data-testid="ai-improve-translate">Traduire</DropdownMenuSubTrigger>
              <DropdownMenuSubContent>
                {LANGS.map(([l, n]) => (
                  <DropdownMenuItem key={l} onSelect={() => setJob({ mode: "translate", language: l, at: Date.now() })} data-testid={`ai-translate-${l}`}>{n}</DropdownMenuItem>
                ))}
              </DropdownMenuSubContent>
            </DropdownMenuSub>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
      <GenerateDialog open={genOpen} onOpenChange={setGenOpen} kind={kind} context={context} onUse={onChange} />
      <ImproveDialog job={job} onClose={() => setJob(null)} kind={kind} value={value} onUse={onChange} />
    </div>
  );
}
